import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';

// Events the backend can send — see backend/src/lib/events.ts.
// Each type maps to a set of TanStack Query keys to invalidate so the UI
// refetches without anyone reloading the page.
const INVALIDATIONS: Record<string, string[]> = {
  'entry.created':        ['entries'],
  'entry.updated':        ['entries'],
  'entry.deleted':        ['entries'],
  'symptom.created':      ['symptoms'],
  'symptom.updated':      ['symptoms'],
  'symptom.deleted':      ['symptoms'],
  'handover.created':     ['handovers'],
  'shift.changed':        ['shifts'],
  'med-schedule.changed': ['med-schedules'],
  'med-reminder.fired':   ['med-schedules'],
  'med-reminder.acked':   ['entries'],
  'document.changed':     ['documents'],
  'meal-idea.changed':    ['meal-ideas'],
  'user.changed':         ['admin', 'users'],
};

// Opens an auto-reconnecting SSE stream for the lifetime of the logged-in
// session. EventSource already retries on network blips, so we just need to
// close it when the user signs out / tab unloads.
export function useLiveEvents(): void {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    const es = new EventSource('/api/events/stream', { withCredentials: true });

    const listeners: Array<[string, (e: MessageEvent) => void]> = [];
    for (const [type, keys] of Object.entries(INVALIDATIONS)) {
      const handler = () => invalidate(qc, keys);
      es.addEventListener(type, handler as EventListener);
      listeners.push([type, handler]);
    }

    return () => {
      for (const [type, handler] of listeners) {
        es.removeEventListener(type, handler as EventListener);
      }
      es.close();
    };
  }, [user, qc]);
}

function invalidate(qc: QueryClient, keys: string[]): void {
  for (const key of keys) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}
